import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./button";
import { cn } from "@/lib/utils";

export interface PaginationProps {
	currentPage: number;
	totalPages: number;
	totalItems: number;
	itemsPerPage: number;
	onPageChange: (page: number) => void;
	onPageSizeChange: (size: number) => void;
	itemName?: string;
	className?: string;
}

/** Build page numbers to show: [1, 2, 3, null, 30, 50, 60, null, 99, 100]. null = ellipsis. */
function getVisiblePageNumbers(currentPage: number, totalPages: number, siblingCount = 1): (number | null)[] {
	if (totalPages <= 0) return [];
	if (totalPages <= 7) {
		return Array.from({ length: totalPages }, (_, i) => i + 1);
	}

	const leftSibling = Math.max(1, currentPage - siblingCount);
	const rightSibling = Math.min(totalPages, currentPage + siblingCount);
	const showLeftEllipsis = leftSibling > 2;
	const showRightEllipsis = rightSibling < totalPages - 1;

	if (!showLeftEllipsis && showRightEllipsis) {
		// e.g. 1, 2, 3, 4, null, 100
		const leftCount = rightSibling + 1;
		return [...Array.from({ length: leftCount }, (_, i) => i + 1), null, totalPages];
	}
	if (showLeftEllipsis && !showRightEllipsis) {
		// e.g. 1, null, 97, 98, 99, 100
		const rightCount = totalPages - leftSibling + 2;
		return [1, null, ...Array.from({ length: rightCount }, (_, i) => totalPages - rightCount + 1 + i)];
	}
	if (showLeftEllipsis && showRightEllipsis) {
		// e.g. 1, null, 49, 50, 51, null, 100
		const middle = Array.from({ length: rightSibling - leftSibling + 1 }, (_, i) => leftSibling + i);
		return [1, null, ...middle, null, totalPages];
	}
	// both ellipsis hidden (small total)
	return Array.from({ length: totalPages }, (_, i) => i + 1);
}

/** 10/20/50/100, plus the page's own default when it is not one of them (e.g. a 12-card grid). */
function pageSizeOptions(current: number): number[] {
	const sizes = [10, 20, 50, 100];
	return sizes.includes(current) ? sizes : [...sizes, current].sort((a, b) => a - b);
}

export const Pagination: React.FC<PaginationProps> = ({
	currentPage,
	totalPages,
	totalItems,
	itemsPerPage,
	onPageChange,
	onPageSizeChange,
	itemName,
	className,
}) => {
	const { t } = useTranslation();

	const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
	const endItem = Math.min(currentPage * itemsPerPage, totalItems);

	const itemNameText = itemName ? t(itemName) : "";

	const visiblePages = getVisiblePageNumbers(currentPage, totalPages, 2);

	return (
		<div className={cn("flex flex-wrap items-center justify-between gap-3", className)}>
			<div className="text-sm text-muted-foreground">
				{t("common.showing")} {startItem} - {endItem} {t("common.of")} {totalItems} {itemNameText}
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<select
					aria-label={t("common.perPage")}
					title={t("common.perPage")}
					value={itemsPerPage}
					onChange={e => {
						onPageSizeChange(Number(e.target.value));
						onPageChange(1);
					}}
					className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				>
					{pageSizeOptions(itemsPerPage).map(size => (
						<option key={size} value={size}>
							{size}
						</option>
					))}
				</select>

				<Button
					variant="outline"
					size="sm"
					className="h-10"
					onClick={() => onPageChange(currentPage - 1)}
					disabled={currentPage === 1}
				>
					{t("common.previous")}
				</Button>

				<span className="text-sm text-muted-foreground sm:hidden">
					{totalPages === 0 ? 0 : currentPage} / {totalPages}
				</span>
				<div className="hidden items-center gap-1 sm:flex">
					{visiblePages.map((page, index) =>
						page === null ? (
							<span
								key={`ellipsis-${index}`}
								className="w-10 h-9 flex items-center justify-center text-muted-foreground px-1"
							>
								…
							</span>
						) : (
							<Button
								key={page}
								variant={currentPage === page ? "default" : "outline"}
								size="sm"
								onClick={() => onPageChange(page)}
								className="h-11 w-11 p-0"
							>
								{page}
							</Button>
						),
					)}
				</div>

				<Button
					variant="outline"
					size="sm"
					className="h-10"
					onClick={() => onPageChange(currentPage + 1)}
					disabled={currentPage >= totalPages}
				>
					{t("common.next")}
				</Button>
			</div>
		</div>
	);
};
